import { AppData, Lightning, Registry } from '@lightningjs/sdk';
import AbstractPlayerEventHandler from './AbstractPlayerEventHandler';
import { secondsToMilliseconds } from 'support/timeUtils';
import { Video } from 'types/api/media';
import {
  canSaveVideoProgress,
  getCreditStartTime,
  isInCredits,
} from 'support/contentUtils';
import {
  reportAdMetadata,
  reportMidPoint,
  reportPlayback,
} from 'services/analytics/reportingServiceVideo';
import { PlaybackEvent } from 'types/analytics';
import { Ad, ImaEvent } from 'types/player';

const DEFAULT_SAVE_HISTORY_HEARTBEAT_SEC = 20;
const MIDPOINT_HEARTBEAT_INTERVAL_SEC = 1;

export default class VodPlayerEventHandler
  extends AbstractPlayerEventHandler
  implements
    Lightning.Component.ImplementTemplateSpec<AbstractPlayerEventHandler>
{
  private _video: Video | null = null;
  private _hasAdEndedEarly = false;
  private _midpointAnalyticsHeartbeatIntervalId: number | null = null;
  private _saveHistoryHeartbeatIntervalId: number | null = null;

  override initializePlayerContent(mediaContent: Video) {
    this._video = mediaContent;

    this.setIsAdPlaying(false);
  }

  override startAnalytics() {
    super.startAnalytics();
    this.startSaveHistoryHeartbeat();
    this.startMidpointAnalyticsHeartbeat();
  }

  override resetAnalytics(isComplete?: boolean) {
    super.resetAnalytics();
    this.stopSaveHistoryHeartbeat(isComplete);
    this.stopMidpointAnalyticsHeartbeat();
  }

  private startMidpointAnalyticsHeartbeat() {
    this._midpointAnalyticsHeartbeatIntervalId = Registry.setInterval(() => {
      if (this._isAdPlaying) return;

      const currentTime = this._Player!.getRelativeTime();
      const isPastMidpoint =
        this._video && currentTime > Number(this._video.durationSecs) / 2;

      if (isPastMidpoint) {
        reportMidPoint(currentTime);
        this.stopMidpointAnalyticsHeartbeat();
      }
    }, secondsToMilliseconds(MIDPOINT_HEARTBEAT_INTERVAL_SEC));
  }

  private stopMidpointAnalyticsHeartbeat() {
    if (this._midpointAnalyticsHeartbeatIntervalId !== null) {
      Registry.clearInterval(this._midpointAnalyticsHeartbeatIntervalId);
      this._midpointAnalyticsHeartbeatIntervalId = null;
    }
  }

  private startSaveHistoryHeartbeat() {
    const saveWatchHistorySeconds =
      AppData?.bookmarkHeartbeatIntervalSeconds ??
      DEFAULT_SAVE_HISTORY_HEARTBEAT_SEC;

    this._saveHistoryHeartbeatIntervalId = Registry.setInterval(() => {
      const isPlaying = this._Player?.isPlaying();
      if (
        this._isAdPlaying ||
        !isPlaying ||
        this._isBuffering ||
        !this._Player ||
        !this._video
      )
        return;
      this.saveProgress();
    }, secondsToMilliseconds(saveWatchHistorySeconds));
  }

  private stopSaveHistoryHeartbeat(isComplete?: boolean) {
    // save last continue watch history
    if (this._saveHistoryHeartbeatIntervalId !== null) {
      this.saveProgress(isComplete);

      Registry.clearInterval(this._saveHistoryHeartbeatIntervalId);
      this._saveHistoryHeartbeatIntervalId = null;
    }
  }

  private saveProgress(isComplete?: boolean) {
    const video = this._video;
    const player = this._Player;
    if (!player || !video) return;

    const saveTime = player.getSaveTime();
    if (!canSaveVideoProgress(video, saveTime)) return;
    if (this._updatingAccountBookmark) return;

    this.updateBookmark(video, saveTime, isComplete);
  }

  private async updateBookmark(
    video: Video,
    saveTime: number,
    isComplete?: boolean,
  ) {
    this._updatingAccountBookmark = true;
    await AppData?.historyService.addOrUpdate(
      this._video,
      saveTime,
      isComplete || isInCredits(video, this._Player!.currentContentTime()),
      this._contentAddedToAccount,
    );
    this._contentAddedToAccount = true;
    this._updatingAccountBookmark = false;
  }

  private handleVodCreditsUpdate(rawTime: number, relativeTime: number) {
    if (!this._Player) return;

    const isInCredits =
      this._video &&
      this._Player.currentContentTime() >= getCreditStartTime(this._video);

    if (isInCredits) {
      this._canCreditEvent &&
        reportPlayback(
          PlaybackEvent.CREDITS_REACHED,
          { relativeTime, rawTime },
          !this._hasReachedCreditsOnce,
        );

      if (!this._hasReachedCreditsOnce) {
        AppData?.historyService.addNextEpisode(this._video);
      }

      this._canCreditEvent = false;
      this._hasReachedCreditsOnce = true;
    } else if (!this._canCreditEvent) {
      this._canCreditEvent = true;
    }
  }

  private handleVodAdEndUpdate(relativeTime: number) {
    if (!this._Player) return;

    const adDuration = this._Player!.adDuration();
    if (this._hasAdEndedEarly && adDuration && relativeTime >= adDuration) {
      this._hasAdEndedEarly = false;
      this.setIsAdPlaying(false);
    }
  }

  private setIsAdPlaying(isAdPlaying: boolean) {
    this._isAdPlaying = isAdPlaying;
    this.signal('$setIsAdPlaying', isAdPlaying);
  }

  private handleAdPodStart() {
    // Prevent calling multiple times
    if (this._isAdPlaying) return;

    // IMA will occasionally send false positive ads. Verify that we are
    // actually in an ad pod
    const isPlayerInAdPod = this._Player?.isPlayerInAdPod();
    if (!isPlayerInAdPod) return;

    // Guard to have ads skipped for Brightcove SSAI.
    // setIsAdPlaying(true) seems to always runs before checking shouldSkipAdPod()
    const currentCuePoint = this._Player!.getCurrentCuePoint();
    if (currentCuePoint && currentCuePoint.played) {
      return;
    }

    this.setIsAdPlaying(true);

    const relativeTime = this._Player!.currentContentTime(); // we pass content time because we know ad time is 0
    const rawTime = this._Player!.currentRawTime();

    reportPlayback(PlaybackEvent.AD_POD_START, { relativeTime, rawTime });
  }

  private handleAdPodEnd(type: string, skipped: boolean) {
    // prevent calling multiple times
    if (!this._isAdPlaying) return;

    const relativeTime = this._Player!.getRelativeTime();
    const rawTime = this._Player!.currentRawTime();
    reportPlayback(
      PlaybackEvent.AD_POD_END,
      { relativeTime, rawTime },
      skipped,
    );

    if (type !== ImaEvent.AD_BREAK_ENDED) {
      this.setIsAdPlaying(false);
      return;
    }

    // if AD_BREAK_EVENT is fired, but ad has not reached the end,
    // rely on $videoPlayerTimeUpdate() to switch the UI
    const adDuration = this._Player!.adDuration();
    const adRelativeTime = this._Player!.getRelativeTime();
    if (adDuration && adRelativeTime >= adDuration) {
      this.setIsAdPlaying(false);
    } else {
      this._hasAdEndedEarly = true;
    }
  }

  /* =============================
   * Events
   * ============================= */

  override $videoPlayerTimeUpdate(rawTime: number) {
    if (!this._Player || !this._video) return;
    const relativeTime = this._Player.getRelativeTime();

    this.handleVodCreditsUpdate(rawTime, relativeTime);
    this.handleVodAdEndUpdate(relativeTime);
    super.$videoPlayerTimeUpdate(rawTime);
  }

  override $videoPlayerSeeked(rawTime: number) {
    super.$videoPlayerSeeked(rawTime);
    this.saveProgress();
  }

  override $videoManagerSkipAdPod(type: string) {
    this.handleAdPodEnd(type, true);
  }

  override $videoManagerCleanupAdPod(type: string) {
    this.handleAdPodEnd(type, false);
  }

  override $videoManagerAdBreakEnded(type: string) {
    this.handleAdPodEnd(type, false);
  }

  override $videoManagerAdBreakStarted() {
    this.handleAdPodStart();
  }

  override $videoManagerStarted(event: unknown) {
    const duration = this._Player!.adDuration();
    const ad: Ad | undefined = this._Player!.extractAd(event);
    if (!ad || duration === null) return;

    const relativeTime = this._Player!.currentContentTime(); // we pass content time because we know ad time is 0
    const rawTime = this._Player!.currentRawTime();

    reportAdMetadata(ad);
    reportPlayback(PlaybackEvent.AD_START, { relativeTime, rawTime }, ad);
  }

  override $videoManagerComplete() {
    const relativeTime = this._Player!.getRelativeTime();
    const rawTime = this._Player!.currentRawTime();

    reportPlayback(PlaybackEvent.AD_END, { relativeTime, rawTime });
  }
}
